<template>
  <div class="feedback-section mt-3 pt-3 border-top">
    <!-- Desktop Layout: Single row with stars, label, and buttons -->
    <div class="d-none d-lg-flex align-items-center gap-3 flex-wrap">
      <!-- Header + Stars -->
      <div class="d-flex align-items-center gap-2">
        <i class="bi bi-chat-left-dots text-muted"></i>
        <span class="small text-muted fw-semibold">Rate:</span>
        <div class="star-rating d-flex gap-1">
          <button
            v-for="star in 5"
            :key="star"
            @click="selectRating(star)"
            @mouseenter="hoverRating = star"
            @mouseleave="hoverRating = 0"
            class="btn btn-sm p-0 border-0"
            :disabled="submitting || submitted"
            :title="getStarLabel(star)"
            style="font-size: 1.5rem; line-height: 1;"
          >
            <i
              :class="star <= (hoverRating || selectedRating) ? 'bi-star-fill text-warning' : 'bi-star text-muted'"
            ></i>
          </button>
        </div>
        <span v-if="selectedRating" class="small text-muted">
          {{ getStarLabel(selectedRating) }}
        </span>
      </div>

      <!-- Comment/Request Button -->
      <button
        @click="showCommentModal = true"
        class="btn btn-sm btn-outline-primary"
        :disabled="submitting || submitted"
      >
        <i class="bi bi-chat-square-text"></i>
        Comment / Request to Pam
      </button>
      <span v-if="hasComment" class="text-success small">
        <i class="bi bi-check-circle-fill"></i> Added
      </span>

      <!-- Submit Button -->
      <button
        v-if="!submitted"
        @click="submitFeedback"
        class="btn btn-sm btn-primary"
        :disabled="submitting || (!selectedRating && !hasComment)"
      >
        <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="bi bi-send"></i>
        {{ submitting ? 'Sending...' : 'Submit' }}
      </button>

      <!-- Success Message -->
      <span v-if="submitted" class="text-success small d-flex align-items-center gap-1">
        <i class="bi bi-check-circle-fill"></i>
        <span>Thanks for your feedback!</span>
      </span>
    </div>

    <!-- Mobile Layout: 3 rows -->
    <div class="d-lg-none">
      <!-- Row 1: Header -->
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="bi bi-chat-left-dots text-muted"></i>
        <span class="small text-muted fw-semibold">Rate this insight</span>
      </div>

      <!-- Row 2: Stars -->
      <div class="d-flex align-items-center gap-3 mb-2">
        <div class="star-rating d-flex gap-1">
          <button
            v-for="star in 5"
            :key="star"
            @click="selectRating(star)"
            @mouseenter="hoverRating = star"
            @mouseleave="hoverRating = 0"
            class="btn btn-sm p-0 border-0"
            :disabled="submitting || submitted"
            :title="getStarLabel(star)"
            style="font-size: 1.5rem; line-height: 1;"
          >
            <i
              :class="star <= (hoverRating || selectedRating) ? 'bi-star-fill text-warning' : 'bi-star text-muted'"
            ></i>
          </button>
        </div>
        <span v-if="selectedRating" class="small text-muted">
          {{ getStarLabel(selectedRating) }}
        </span>
      </div>

      <!-- Row 3: Buttons -->
      <div class="d-flex gap-2 align-items-center flex-wrap">
        <button
          @click="showCommentModal = true"
          class="btn btn-sm btn-outline-primary"
          :disabled="submitting || submitted"
        >
          <i class="bi bi-chat-square-text"></i>
          Comment / Request to Pam
        </button>
        <span v-if="hasComment" class="text-success small">
          <i class="bi bi-check-circle-fill"></i> Comment added
        </span>
        <button
          v-if="!submitted"
          @click="submitFeedback"
          class="btn btn-sm btn-primary"
          :disabled="submitting || (!selectedRating && !hasComment)"
        >
          <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-send"></i>
          {{ submitting ? 'Sending...' : 'Submit Feedback' }}
        </button>
      </div>

      <!-- Success Message -->
      <div v-if="submitted" class="text-success small d-flex align-items-center gap-1 mt-2">
        <i class="bi bi-check-circle-fill"></i>
        <span>Thanks for your feedback!</span>
      </div>
    </div>

    <!-- Error Message (both layouts) -->
    <div v-if="errorMessage" class="alert alert-danger alert-sm mt-2 mb-0 py-1 px-2 small">
      <i class="bi bi-exclamation-circle-fill"></i> {{ errorMessage }}
    </div>

    <!-- Comment/Request Modal -->
    <div
      v-if="showCommentModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5);"
      @click.self="showCommentModal = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-chat-square-text text-primary"></i>
              Share Your Thoughts with Pam
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showCommentModal = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Request Type Selection -->
            <div class="mb-3">
              <label class="form-label small fw-semibold">What type of feedback is this?</label>
              <div class="d-flex flex-wrap gap-2">
                <button
                  v-for="type in requestTypes"
                  :key="type.value"
                  @click="requestType = type.value"
                  class="btn btn-sm"
                  :class="requestType === type.value ? 'btn-primary' : 'btn-outline-secondary'"
                >
                  <i :class="type.icon"></i> {{ type.label }}
                </button>
              </div>
            </div>

            <!-- Comment Text Area -->
            <div class="mb-3">
              <label for="feedback-comment" class="form-label small fw-semibold">
                Your feedback or question
              </label>
              <textarea
                id="feedback-comment"
                v-model="feedbackComment"
                class="form-control"
                rows="4"
                :placeholder="getCommentPlaceholder()"
              ></textarea>
            </div>

            <!-- Helpful Tips -->
            <div class="alert alert-info small mb-0">
              <i class="bi bi-lightbulb"></i> <strong>Tips for better feedback:</strong>
              <ul class="mb-0 mt-1 ps-3">
                <li>Be specific about what data or insight you're referring to</li>
                <li>Include context: dates, metrics, or trends you're questioning</li>
                <li>Describe what action you want to take (if applicable)</li>
                <li>Ask clear questions that Pam can answer with data</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="showCommentModal = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              @click="saveComment"
              :disabled="!feedbackComment.trim() || !requestType"
            >
              <i class="bi bi-check-lg"></i> Save Comment
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/api/http'

export default {
  name: 'FeedbackButtons',
  props: {
    section: {
      type: String,
      required: true,
      // e.g., 'booking_revenue', 'occupancy', 'call_center', 'social_reputation'
    },
    locationId: {
      type: Number,
      required: true
    },
    materializationId: {
      type: Number,
      default: null
    },
    sectionId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      selectedRating: 0,
      hoverRating: 0,
      submitted: false,
      submitting: false,
      feedbackComment: '',
      requestType: null,
      showCommentModal: false,
      errorMessage: null,
      requestTypes: [
        { value: 'question', label: 'Question', icon: 'bi-question-circle' },
        { value: 'clarification', label: 'Clarification', icon: 'bi-info-circle' },
        { value: 'feature_request', label: 'Feature Request', icon: 'bi-lightbulb' },
        { value: 'data_issue', label: 'Data Issue', icon: 'bi-exclamation-triangle' },
        { value: 'general_feedback', label: 'General', icon: 'bi-chat' }
      ]
    }
  },
  computed: {
    hasComment() {
      return this.feedbackComment.trim().length > 0 && this.requestType !== null
    }
  },
  methods: {
    selectRating(rating) {
      if (this.submitted) return
      this.selectedRating = rating
      this.errorMessage = null
    },

    getStarLabel(rating) {
      const labels = {
        1: 'Not helpful',
        2: 'Somewhat helpful',
        3: 'Helpful',
        4: 'Very helpful',
        5: 'Extremely helpful'
      }
      return labels[rating] || ''
    },

    getCommentPlaceholder() {
      const placeholders = {
        question: 'What question do you have about this insight? Be specific about the data or trend you want to understand.',
        clarification: 'What part of this insight is unclear or confusing? Help Pam improve the explanation.',
        feature_request: 'What additional insight or feature would be helpful? Describe what you\'d like to see.',
        data_issue: 'What data appears incorrect or missing? Include specific numbers or dates if possible.',
        general_feedback: 'Share your thoughts, suggestions, or general feedback about this insight.'
      }
      return placeholders[this.requestType] || 'Type your feedback here...'
    },

    saveComment() {
      if (!this.feedbackComment.trim() || !this.requestType) return
      this.showCommentModal = false
    },

    async submitFeedback() {
      if (this.submitted) return

      // Must have either rating or comment
      if (!this.selectedRating && !this.hasComment) {
        this.errorMessage = 'Please provide a star rating or comment.'
        return
      }

      this.submitting = true
      this.errorMessage = null

      try {
        const payload = {
          materialization_type: 'location',
          materialization_id: this.materializationId,
          section_id: this.sectionId,
          vote: this.hasComment ? 'comment_request' : 'star_rating'
        }

        // Add star rating if provided
        if (this.selectedRating) {
          payload.star_rating = this.selectedRating
        }

        // Add comment and request type if provided
        if (this.hasComment) {
          payload.comment = this.feedbackComment.trim()
          payload.request_type = this.requestType
        }

        await http.post('/api/v1/feedback/vote', payload)

        this.submitted = true
        this.$emit('feedback-submitted', {
          section: this.section,
          rating: this.selectedRating,
          comment: this.feedbackComment.trim(),
          requestType: this.requestType
        })
      } catch (error) {
        console.error('Failed to submit feedback:', error)
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('already provided feedback')) {
          this.errorMessage = 'You already provided feedback for this insight today.'
          this.submitted = true
        } else {
          this.errorMessage = error.response?.data?.detail || 'Failed to submit feedback. Please try again.'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.star-rating button {
  transition: transform 0.1s ease;
}

.star-rating button:hover:not(:disabled) {
  transform: scale(1.1);
}

.star-rating button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal.show {
  display: block;
}
</style>
